<template>
  <div class="login-page">
    <div class="login-card">
      <!-- 左侧品牌区 -->
      <div class="brand-pane">
        <div class="brand-bg">
          <div class="grid-lines"></div>
          <div class="glow glow-1"></div>
          <div class="glow glow-2"></div>
          <span v-for="i in 8" :key="i" class="particle" :style="particleStyle(i)"></span>
        </div>

        <div class="brand-logo">
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
        </div>

        <div class="brand-title">crm.fun</div>
        <div class="brand-sub">教育机构经营管理平台</div>

        <ul class="feature-list">
          <li>
            <span class="check">✓</span>
            线索 · 订单 · 账单 · 业绩 一站式管理
          </li>
          <li>
            <span class="check">✓</span>
            机构 / 战队 / 小组 / 员工 多级权限
          </li>
          <li>
            <span class="check">✓</span>
            分期收款 · 退款核算 · 数据报表
          </li>
        </ul>
      </div>

      <!-- 右侧表单区 -->
      <div class="form-pane">
        <div class="form-head">
          <h1 class="form-title">欢迎回来</h1>
          <p class="form-sub">请使用您的账号登录系统</p>
        </div>

        <form class="login-form" @submit.prevent="doLogin">
          <div class="field" :class="{ 'field-focus': focusField === 'account' }">
            <svg class="field-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <input
              v-model="form.account"
              type="text"
              placeholder="账号（机构 / 战队 / 小组 / 员工）"
              maxlength="50"
              autocomplete="username"
              @focus="focusField = 'account'"
              @blur="focusField = ''"
            />
          </div>

          <div class="field" :class="{ 'field-focus': focusField === 'password' }">
            <svg class="field-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <input
              v-model="form.password"
              :type="showPwd ? 'text' : 'password'"
              placeholder="密码"
              maxlength="32"
              autocomplete="current-password"
              @focus="focusField = 'password'"
              @blur="focusField = ''"
            />
            <button
              type="button"
              class="pwd-eye"
              :class="{ 'is-on': showPwd }"
              :aria-label="showPwd ? '隐藏密码' : '显示密码'"
              @click="showPwd = !showPwd"
            >
              <svg v-if="!showPwd" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>

          <div class="row-actions">
            <label class="remember">
              <input v-model="remember" type="checkbox" />
              <span class="custom-check"></span>
              <span class="remember-text">记住账号</span>
            </label>
            <a class="forgot" href="javascript:;">忘记密码？</a>
          </div>

          <button class="login-btn" type="submit" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            <span>{{ loading ? '登录中…' : '登 录' }}</span>
          </button>
        </form>

        <p class="login-tip">忘记密码请联系机构管理员重置</p>
      </div>
    </div>

    <footer class="login-footer">© 2026 crm.fun · 教育机构经营管理平台</footer>
  </div>
</template>

<script>
import { login as apiLogin } from '@/api/auth'

export default {
  name: 'Login',
  data() {
    return {
      form: { account: '', password: '' },
      showPwd: false,
      remember: true,
      loading: false,
      focusField: ''
    }
  },
  created() {
    // 已登录直接进工作台
    if (localStorage.getItem('crm_token')) {
      this.$router.replace('/dashboard')
      return
    }
    // 记住账号：回填上次保存的账号
    try {
      const saved = localStorage.getItem('crm_remember_account')
      if (saved) this.form.account = saved
    } catch (e) { /* ignore */ }
  },
  methods: {
    particleStyle(i) {
      return {
        left: `${8 + ((i * 13) % 84)}%`,
        top: `${15 + ((i * 29) % 70)}%`,
        animationDelay: `${(i * 0.7) % 6}s`,
        animationDuration: `${5 + (i % 4)}s`
      }
    },
    doLogin() {
      if (this.loading) return
      const account = (this.form.account || '').trim()
      if (!account) return this.$message.error('请输入账号')
      if (!this.form.password) return this.$message.error('请输入密码')

      // 记住账号
      try {
        if (this.remember) localStorage.setItem('crm_remember_account', account)
        else localStorage.removeItem('crm_remember_account')
      } catch (e) { /* ignore */ }

      this.loading = true
      apiLogin({ account, password: this.form.password })
        .then(data => {
          this.$store.dispatch('setSession', { token: data.token, user: data })
          this.$message.success('登录成功')
          this.$router.replace('/dashboard')
        })
        .catch(() => { /* 错误提示已由拦截器统一处理 */ })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
/* ===== 全局变量 ===== */
.login-page {
  --brand: #3668FC;
  --brand-2: #5B8DFF;
  --brand-dark: #1B2A4D;
  --ink: #18233C;
  --muted: #8A94AD;
  --border: #E3E8F4;

  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  position: relative;
  font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif;
  background:
    radial-gradient(1200px 600px at 85% -10%, rgba(54, 104, 252, 0.08), transparent 60%),
    radial-gradient(900px 500px at -10% 110%, rgba(91, 141, 255, 0.10), transparent 60%),
    linear-gradient(160deg, #EEF3FF 0%, #F6F8FD 50%, #EDF3FE 100%);
}

/* ===== 登录卡片 ===== */
.login-card {
  position: relative;
  width: 100%;
  max-width: 400px;
  display: flex;
  background: #FFFFFF;
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 24px 60px rgba(27, 42, 77, 0.12),
    0 4px 12px rgba(27, 42, 77, 0.05);
  animation: cardIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* ===== 左侧品牌区（桌面显示） ===== */
.brand-pane {
  display: none;
}

/* ===== 右侧表单区 ===== */
.form-pane {
  flex: 1;
  padding: 44px 34px 30px;
  box-sizing: border-box;
}

.form-head {
  margin-bottom: 28px;
}

.form-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: 1px;
}

.form-sub {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--muted);
}

/* 表单字段 */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #FBFCFE;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.field:hover {
  border-color: #C9D4EF;
}

.field-focus {
  border-color: var(--brand);
  background: #FFFFFF;
  box-shadow: 0 0 0 3px rgba(54, 104, 252, 0.12);
}

.field-icon {
  flex-shrink: 0;
  color: #9AA5BF;
  transition: color 0.25s;
  margin-right: 10px;
}

.field-focus .field-icon {
  color: var(--brand);
}

.field input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--ink);
}

.field input::placeholder {
  color: #B4BDCF;
}

.pwd-eye {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #B4BDCF;
  transition: color 0.2s;
}

.pwd-eye:hover {
  color: var(--muted);
}

.pwd-eye.is-on {
  color: var(--brand);
}

/* 记住我 / 忘记密码 */
.row-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -4px;
}

.remember {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.remember input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.custom-check {
  width: 16px;
  height: 16px;
  border: 1.5px solid #C3CCE0;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-right: 7px;
}

.remember input:checked + .custom-check {
  background: var(--brand);
  border-color: var(--brand);
}

.remember input:checked + .custom-check::after {
  content: '';
  width: 8px;
  height: 5px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translate(0.5px, -1px);
}

.remember-text {
  font-size: 13px;
  color: var(--muted);
}

.forgot {
  font-size: 13px;
  color: var(--brand);
  text-decoration: none;
  transition: opacity 0.2s;
}

.forgot:hover {
  opacity: 0.75;
}

/* 登录按钮 */
.login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 4px;
  color: #fff;
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  box-shadow: 0 10px 24px rgba(54, 104, 252, 0.32);
  transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
  margin-top: 2px;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(54, 104, 252, 0.40);
  filter: brightness(1.03);
}

.login-btn:active:not(:disabled) {
  transform: translateY(0);
}

.login-btn:disabled {
  cursor: not-allowed;
  opacity: 0.85;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.login-tip {
  margin: 20px 0 0;
  text-align: center;
  font-size: 12px;
  color: #B4BDCF;
}

/* ===== 页脚 ===== */
.login-footer {
  position: absolute;
  bottom: 18px;
  font-size: 12px;
  color: #A8B2C9;
  letter-spacing: 0.5px;
}

/* ===== 桌面端：双栏 ===== */
@media (min-width: 992px) {
  .login-card {
    max-width: 880px;
    border-radius: 20px;
  }

  .brand-pane {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 360px;
    flex-shrink: 0;
    padding: 52px 42px;
    color: #fff;
    background: linear-gradient(160deg, #16213D 0%, #1B2A4D 55%, #24418A 130%);
    position: relative;
    overflow: hidden;
  }

  /* 品牌区背景装饰 */
  .brand-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .grid-lines {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 44px 44px;
    mask-image: radial-gradient(circle at 30% 30%, #000 0%, transparent 75%);
    -webkit-mask-image: radial-gradient(circle at 30% 30%, #000 0%, transparent 75%);
  }

  .glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(10px);
  }

  .glow-1 {
    width: 260px;
    height: 260px;
    right: -90px;
    top: -80px;
    background: radial-gradient(circle, rgba(91, 141, 255, 0.35), transparent 70%);
  }

  .glow-2 {
    width: 240px;
    height: 240px;
    left: -70px;
    bottom: -90px;
    background: radial-gradient(circle, rgba(54, 104, 252, 0.30), transparent 70%);
  }

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(160, 190, 255, 0.7);
    animation: float linear infinite;
  }

  .brand-logo {
    position: relative;
    z-index: 1;
    width: 54px;
    height: 54px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, var(--brand), var(--brand-2));
    box-shadow: 0 12px 28px rgba(54, 104, 252, 0.5);
    margin-bottom: 24px;
    animation: rise 0.7s 0.15s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .brand-title {
    position: relative;
    z-index: 1;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 2px;
    animation: rise 0.7s 0.25s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .brand-sub {
    position: relative;
    z-index: 1;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.62);
    margin-top: 10px;
    letter-spacing: 1px;
    animation: rise 0.7s 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .feature-list {
    position: relative;
    z-index: 1;
    list-style: none;
    margin: 34px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .feature-list li {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.78);
    animation: rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .feature-list li:nth-child(1) { animation-delay: 0.45s; }
  .feature-list li:nth-child(2) { animation-delay: 0.55s; }
  .feature-list li:nth-child(3) { animation-delay: 0.65s; }

  .check {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(91, 141, 255, 0.25);
    color: #9DBBFF;
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .form-pane {
    padding: 56px 52px 40px;
  }

  .form-title {
    font-size: 26px;
  }
}

/* ===== 动画 ===== */
@keyframes cardIn {
  from { opacity: 0; transform: translateY(24px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes rise {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes float {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 0.7; }
  50% { transform: translateY(-30px) translateX(8px); opacity: 0.5; }
  90% { opacity: 0.2; }
  100% { transform: translateY(-60px) translateX(-6px); opacity: 0; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
